/**
 * Live Stream Countdown Component
 * Displays a countdown timer until the scheduled start time
 */
export class LiveStreamCountdown extends HTMLElement {
  static get observedAttributes() {
    return ['scheduled-time', 'stream-title'];
  }

  private timerId: ReturnType<typeof setInterval> | null = null;
  private titleEl: HTMLElement | null = null;
  private timerEl: HTMLElement | null = null;
  private containerEl: HTMLElement | null = null;
  private boundVisibilityHandler: () => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.render();
    this.startTimer();
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  disconnectedCallback() {
    this.stopTimer();
    document.removeEventListener(
      'visibilitychange',
      this.boundVisibilityHandler,
    );
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.startTimer();
    } else {
      this.stopTimer();
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      if (name === 'stream-title' && this.titleEl) {
        this.updateTitle();
      }
      if (name === 'scheduled-time') {
        this.render();
        this.startTimer();
      }
    }
  }

  private render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    text-align: center;
                    color: #fff;
                    font-family: inherit;
                    line-height: 1.2;
                }
                .countdown-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .countdown-title {
                    display: block;
                    font-size: 1.25em;
                    margin: 0 0 0.5em 0;
                    opacity: 0.9;
                }
                .countdown-timer {
                    display: block;
                    font-size: 2.5em;
                    font-weight: 700;
                    font-feature-settings: "tnum";
                    font-variant-numeric: tabular-nums;
                    margin: 0;
                }
            </style>
            <div class="countdown-container">
                <div class="countdown-title"></div>
                <div class="countdown-timer"></div>
            </div>
        `;

    this.containerEl = this.shadowRoot.querySelector('.countdown-container');
    this.titleEl = this.shadowRoot.querySelector('.countdown-title');
    this.timerEl = this.shadowRoot.querySelector('.countdown-timer');

    this.updateTitle();
  }

  private updateTitle() {
    if (this.titleEl) {
      const title = this.getAttribute('stream-title') || 'Live stream';
      this.titleEl.textContent = `${title} will start in`;
    }
  }

  private startTimer() {
    this.stopTimer();
    this.update();
    this.timerId = setInterval(() => this.update(), 1000);
  }

  private stopTimer() {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private update() {
    if (!this.containerEl || !this.timerEl) return;

    const scheduledTimeStr = this.getAttribute('scheduled-time');
    if (!scheduledTimeStr) {
      this.containerEl.style.display = 'none';
      return;
    }

    this.containerEl.style.display = 'flex';

    const targetDate = new Date(scheduledTimeStr).getTime();
    const now = new Date().getTime();
    const distance = targetDate - now;

    if (distance < 0) {
      if (this.containerEl) {
        this.containerEl.innerHTML = 'Starting soon...';
      }
      this.stopTimer();
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    let timeText = '';
    if (days > 0) timeText += `${days} days `;
    timeText += `${hours}:${minutes.toString().padStart(2, '0')}:${
      seconds.toString().padStart(2, '0')
    }`;

    this.timerEl.textContent = timeText;
  }
}

if (!customElements.get('live-stream-countdown')) {
  customElements.define('live-stream-countdown', LiveStreamCountdown);
}
